import { ChevronLeft, ChevronRight } from 'lucide-react';
import { interactive } from '@/components/ui/interactive';
import { pageCount, pageWindow } from '@/lib/table';
import { cn } from '@/lib/utils';

export interface AdminPaginationProps {
  page: number;
  perPage: number;
  total: number;
  onPageChange: (page: number) => void;
  /** e.g. "عرض ٦ من ١٢ كادر" — already interpolated by the caller. */
  summary: string;
  previousLabel: string;
  nextLabel: string;
  pageLabel: (page: number) => string;
}

/**
 * The table footer pager from the prototype.
 *
 * One deliberate deviation: the prototype points the "previous" chevron left and "next"
 * right, which is the LTR convention left in place under `dir="rtl"` — in Arabic the
 * earlier page is to the *right*. Both icons carry `rtl:rotate-180`, so they point the
 * way the reader travels in either direction. The labels are translated strings, so a
 * screen reader never has to infer meaning from the glyph at all.
 */
export function AdminPagination({
  page,
  perPage,
  total,
  onPageChange,
  summary,
  previousLabel,
  nextLabel,
  pageLabel,
}: AdminPaginationProps) {
  const pages = pageCount(total, perPage);
  if (total === 0) return null;

  const stepClass = cn(
    interactive,
    'border-conc-100 text-conc-600 hover:border-navy grid h-8 w-8 place-items-center rounded-md border disabled:opacity-40 disabled:hover:border-conc-100',
  );

  return (
    <nav
      aria-label={summary}
      className="flex flex-wrap items-center justify-between gap-3 border-t border-conc-100 px-5 py-3"
    >
      <p className="num text-[12.5px] text-conc-500">{summary}</p>

      <div className="flex items-center gap-1">
        <button
          type="button"
          aria-label={previousLabel}
          disabled={page <= 1}
          onClick={() => onPageChange(page - 1)}
          className={stepClass}
        >
          <ChevronLeft aria-hidden className="h-4 w-4 rtl:rotate-180" />
        </button>

        {pageWindow(page, pages).map((candidate) => (
          <button
            key={candidate}
            type="button"
            aria-label={pageLabel(candidate)}
            aria-current={candidate === page ? 'page' : undefined}
            onClick={() => onPageChange(candidate)}
            className={cn(
              interactive,
              'num h-8 min-w-8 rounded-md px-2 text-[12.5px] font-bold',
              candidate === page
                ? 'bg-pitch text-white'
                : 'border border-conc-100 text-conc-600 hover:border-navy',
            )}
          >
            {candidate}
          </button>
        ))}

        <button
          type="button"
          aria-label={nextLabel}
          disabled={page >= pages}
          onClick={() => onPageChange(page + 1)}
          className={stepClass}
        >
          <ChevronRight aria-hidden className="h-4 w-4 rtl:rotate-180" />
        </button>
      </div>
    </nav>
  );
}
